import { ChangeEvent, FormEvent, MouseEvent, useMemo, useState } from 'react'

import ReportGmailerrorred from '@mui/icons-material/ReportGmailerrorred'
import Visibility from '@mui/icons-material/Visibility'
import { Alert, InputAdornment, Stack } from '@mui/material'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import TextField from '@mui/material/TextField'

import { streamingLoginStrategy } from './strategies/streaming'
import { LoginForgotPassActiveForm, useCredentials } from './useLogin/useCredentials'
import type { PasswordInputType } from './constants'
import { PASSWORD_INPUT_TYPES } from './constants'
import { StrategyLoginForm } from './StrategyLoginForm'

export const LoginForm = () => {
  const { signIn, platform, onActiveFormUpdate } = useCredentials()
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [passwordInputType, setPasswordInputType] = useState<PasswordInputType>(
    PASSWORD_INPUT_TYPES.PASSWORD
  )
  const [error, setError] = useState<Error>()
  const [isLoading, setIsLoading] = useState(false)

  const loginStrategy = useMemo(() => {
    if (streamingLoginStrategy.shouldUseStrategy(platform)) {
      return streamingLoginStrategy
    }
    return null
  }, [platform])

  if (loginStrategy) {
    return <StrategyLoginForm strategy={loginStrategy} />
  }

  const onEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
  }
  const onPasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value)
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!email || !password) {
      return
    }
    setError(undefined)
    setIsLoading(true)

    try {
      await signIn(email, password, platform)
    } catch (e) {
      setError(e as Error)
    } finally {
      setIsLoading(false)
    }
  }

  const onForgotPassword = async () => {
    onActiveFormUpdate(LoginForgotPassActiveForm.FORGOT_PASSWORD)
  }

  const onMouseDown = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setPasswordInputType(PASSWORD_INPUT_TYPES.TEXT)
  }
  const onMouseUp = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setPasswordInputType(PASSWORD_INPUT_TYPES.PASSWORD)
  }

  return (
    <form onSubmit={onSubmit}>
      <Stack
        sx={{
          '& .MuiFormControl-root': {
            backgroundColor: '#f2f2f2'
          }
        }}
      >
        {error && (
          <Alert variant={'outlined'} color={'error'} sx={{ mb: 3 }} icon={<ReportGmailerrorred />}>
            {error.message}
          </Alert>
        )}
        <TextField
          required
          type={'email'}
          value={email}
          onChange={onEmailChange}
          label={'Address email'}
          fullWidth
        />
        <TextField
          required
          value={password}
          onChange={onPasswordChange}
          type={passwordInputType}
          label={'Password'}
          fullWidth
          sx={{ mt: 3 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position={'end'}>
                <IconButton edge={'end'} onMouseDown={onMouseDown} onMouseUp={onMouseUp}>
                  <Visibility />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <Link
          href={'#'}
          variant="body2"
          underline={'hover'}
          style={{ textAlign: 'right' }}
          onClick={onForgotPassword}
        >
          Forgot password
        </Link>
        <Button
          type={'submit'}
          variant={'contained'}
          color={'primary'}
          sx={{ mt: 4 }}
          disabled={isLoading || !email || !password}
          startIcon={isLoading && <CircularProgress size={24} />}
        >
          Submit
        </Button>
      </Stack>
    </form>
  )
}
